import MuiTabular from 'modules/common/components/mui-tabular';

import { useEffect, useMemo, useRef, useState } from 'react';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { Card, Typography, Grid, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CollapseIcon from 'modules/common/components/collapse-icon';
import {
  selectHotelName,
  selectLastPartialBookingDate,
  selectLocalCurrency,
  selectPartialHours,
  selectPmsSync,
  selectResetBookingDate,
  selectTargetDate,
  selectWorkbookDateType,
} from 'modules/dashboard/selectors';
import {
  selectAuthUser,
  selectOrganizationId,
  selectSessionId,
} from 'modules/common/auth/selectors';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useDebouncedCallback } from 'use-debounce';
import { ENVIRONMENT } from 'config';
import { AssistantIcon } from 'modules/common/components';
import { Box } from '@mui/system';
import { EXPORT_OPTIONS } from 'modules/dashboard/constants';
import {
  IATA_COLUMNS,
  CONSORTIA_COLUMNS,
  RATE_COLUMN_INFO_MAP,
  CONSORTIA_TABLE_COLUMNS,
  IATA_TABLE_COLUMNS,
  CONSORTIA_TABULAR_TITLE,
  IATA_TABULAR_TITLE,
} from '../../../../constants';
import {
  buildConsortiaFilters,
  buildConsortiaHeaderData,
  handleCeruleanLogging,
} from '../../../../function';
import {
  CONSORTIA_CARD_CONTENT_STYLE,
  TABULAR_CARD_STYLE,
  CONSORTIA_HEADER_STYLE,
  CONSORTIA_ROW_STYLE,
  EXPORT_PDF_BUTTON_STYLE,
  exportPdfTextStyle,
} from '../../../../style';
import {
  selectDateComparison,
  selectConsortiaData,
  selectSymmetricComparisonToggle,
  selectWorkbookAlignmentToggle,
  selectWorkbookBookingDate,
  selectWorkbookEndDate,
  selectWorkbookStartDate,
  selectIsDetailed,
} from '../../../../selectors';
import ColumnInfo from '../column-info/column-info';
import { workbookActions } from '../../../../slice';


const MuiConsortiaVisual = ({ isDisplay, onClick, enableSecondaryHotelGroups = false }) => {
  const bookingDate = useSelector(selectWorkbookBookingDate);
  const pms = useSelector(selectPmsSync);
  const targetDate = useSelector(selectTargetDate);
  const selectedDateType = useSelector(selectWorkbookDateType);
  const organizationId = useSelector(selectOrganizationId);
  const hotels = useSelector(selectHotelName);
  const alignmentToggle = useSelector(selectWorkbookAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  const dateComparison = useSelector(selectDateComparison);
  const currencyType = useSelector(selectLocalCurrency);
  const endDate = useSelector(selectWorkbookEndDate);
  const startDate = useSelector(selectWorkbookStartDate);
  const user = useSelector(selectAuthUser);
  const sessionId = useSelector(selectSessionId);
  const consortiaData = useSelector(selectConsortiaData);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const partialHours = useSelector(selectPartialHours);
  const lastBookingDate = useSelector(selectLastPartialBookingDate);
  const isDetailed = useSelector(selectIsDetailed);
  const dispatch = useDispatch();
  //
  const tableRef = useRef();
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  //
  const fetchData = useDebouncedCallback(async () => {
    let start;
    // Build the filters for the API request
    const filters = buildConsortiaFilters({
      bookingDate,
      targetDate,
      dateComparison,
      pms,
      hotels,
      alignmentToggle,
      symmetricComparisonToggle,
      currencyType,
      startDate,
      endDate,
      resetBookingDate,
      partialHours,
      lastBookingDate,
      selectedDateType: selectedDateType?.id,
    });
    try {
      setIsLoading(true);
      // Log filter payload
      handleCeruleanLogging({
        message: `Consortia Stay Tabular Filters: ${JSON.stringify(filters)}`,
        sessionId,
        user,
        eventType: 'Cerulean Filters',
      });
      // Log request start
      handleCeruleanLogging({
        message: `Consortia Stay Tabular Request start: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loading',
      });
      start = performance.now();
      setExportData({
        columns: isDetailed ? IATA_COLUMNS : CONSORTIA_COLUMNS,
        filters,
        title: 'mui-rate-plan-visual',
      });
      dispatch(
        workbookActions.getConsortiaData({
          organizationId,
          columns: isDetailed ? IATA_COLUMNS : CONSORTIA_COLUMNS,
          filters,
        })
      );
    } catch (error) {
      handleCeruleanLogging({
        message: `Error fetching Consortia Stay tabular consortia: ${error}`,
        sessionId,
        user,
        eventType: 'Cerulean Error',
      });
    } finally {
      const end = performance.now();

      handleCeruleanLogging({
        message: `Consortia Stay Tabular Request finished: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loaded',
      });

      handleCeruleanLogging({
        message: `Consortia Stay Tabular Request time: ${((end - start) / 1000).toFixed(
          2
        )} seconds`,
        sessionId,
        user,
        eventType: 'Cerulean Request Time',
      });
    }
  }, 500);
  //
  useEffect(() => {
    if (!isDisplay) {
      return;
    }
    fetchData();
  }, [
    isDisplay,
    selectedDateType,
    pms,
    targetDate,
    bookingDate,
    hotels,
    currencyType,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    startDate,
    endDate,
    lastBookingDate,
    partialHours,
    isDetailed,
  ]);

  useEffect(() => {
    if (consortiaData && isLoading) {
      setIsLoading(false);
    }
  }, [consortiaData]);

  const headerData = useMemo(
    () =>
      buildConsortiaHeaderData({
        bookingDate,
        targetDate,
        alignmentToggle,
        symmetricComparisonToggle,
        currencyType,
        aligned: alignmentToggle,
        selectedDateType: selectedDateType?.id,
        dateComparison,
        startDate,
        endDate,
        resetBookingDate,
        lastBookingDate,
        partialHours,
      }),
    [
      bookingDate,
      targetDate,
      alignmentToggle,
      selectedDateType?.id,
      symmetricComparisonToggle,
      currencyType,
      dateComparison,
      startDate,
      endDate,
      resetBookingDate,
      partialHours,
      lastBookingDate,
      isDetailed,
    ]
  );
  //
  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="flex-start"
      className={styles.mainGrid}
      mb={10}
    >
      <Card sx={TABULAR_CARD_STYLE}>
        <Box
          display="flex"
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          height={40}
          pl={2}
        >
          <Typography>{isDetailed ? IATA_TABULAR_TITLE : CONSORTIA_TABULAR_TITLE}</Typography>
          <Box display="flex" alignItems="center">
            {ENVIRONMENT.IS_VISUAL_INTERPRETER_ENABLED?.toLowerCase() === 'true' && (
              <Grid item mt={1} mr={4}>
                <AssistantIcon
                  exportData={exportData}
                  visualName={isDetailed ? IATA_TABULAR_TITLE : CONSORTIA_TABULAR_TITLE}
                  isProcessing={isExporting}
                  setIsProcessing={setIsExporting}
                  disabled={isProcessing || isLoading || isExporting}
                  isCerulean
                  enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                  getExcelExportData={tableRef?.current?.getExcelData}
                  exportType={exportType}
                  setExportType={setExportType}
                />
              </Grid>
            )}
            <>
              <Typography sx={exportPdfTextStyle(isLoading)}>Export Data</Typography>
              <IconButton
                size="small"
                onClick={() => setIsProcessing(true)}
                disabled={isLoading}
                sx={EXPORT_PDF_BUTTON_STYLE}
              >
                <GetAppIcon />
              </IconButton>
            </>
            <CollapseIcon isDisplay={isDisplay} onClick={onClick} />
          </Box>
        </Box>
        {isDisplay && (
          <Grid sx={CONSORTIA_CARD_CONTENT_STYLE}>
            {!consortiaData ? (
              <Grid container justifyContent="center" alignItems="center" m={5}>
                <Typography fontWeight={500}>Loading Data...</Typography>
              </Grid>
            ) : (
              <>
                <MuiTabular
                  ref={tableRef}
                  columns={isDetailed ? IATA_TABLE_COLUMNS : CONSORTIA_TABLE_COLUMNS}
                  data={consortiaData}
                  isAggregate={false}
                  isProcessing={isProcessing}
                  isLoadingData={isLoading}
                  setIsProcessing={setIsProcessing}
                  defaultOrderBy="room_nights_cp"
                  defaultOrder="desc"
                  headerComponent={
                    <Grid container spacing={2} mt={1} pl={1}>
                      {headerData?.map((section, index) => (
                        <Grid item xs={12} sm={6} md={4} key={`${section?.id}-${index.toString()}`}>
                          <Typography
                            fontSize={11}
                            fontFamily="'Segoe UI', wf_segoe-ui_normal, helvetica, arial, sans-serif"
                          >
                            {section?.title}: {section?.value}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  }
                  headerFiltersInfo={{
                    title: isDetailed ? IATA_TABULAR_TITLE : CONSORTIA_TABULAR_TITLE,
                    sections: headerData,
                  }}
                  fileName={isDetailed ? 'IATA_Details_On' : 'IATA_Details_Off'}
                  customHeaderStyle={CONSORTIA_HEADER_STYLE}
                  customRowStyle={CONSORTIA_ROW_STYLE}
                  primaryTextColor="#000"
                  secondaryTextColor="#000"
                />
                <ColumnInfo columnInfoMap={RATE_COLUMN_INFO_MAP} />
              </>
            )}
          </Grid>
        )}
      </Card>
    </Grid>
  );
};

export default MuiConsortiaVisual;
